import { useCallback, useEffect, useState } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  FilterOutlined,
  BankOutlined,
  FundOutlined,
  PrinterOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  BarsOutlined,
  CaretRightOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { MdReceiptLong } from 'react-icons/md';
import { useToast } from '../../hooks/toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IFinancial,
  IPaymentMethod,
  ISessionFilter,
} from '../../shared/interfaces';
import api from '../../services/api';
import {
  Col,
  MenuProps,
  Row,
  Statistic,
  Tag,
  Tooltip,
  Input,
  Select,
  DatePicker,
  Button,
  Dropdown,
  Collapse,
  theme,
} from 'antd';
import { currency } from '../../shared/utils/functions';
import {
  formatDate,
  getInitialFinalDateMonth,
} from '../../shared/utils/format-date';
import FinancialPayment from './Payment';
import Cancel from './Cancel';
import {
  ATable,
  DropdownOptions,
  PageIntro,
  PagePanel,
} from '../../components';
import { saveFilter, getFilter, deleteFilter } from './Others/session-filter';
import {
  FinancialsLayout,
  FinancialsToolbar,
  StatisticCardContainer,
  SummaryGrid,
  TextTable,
} from './styles';
import FinancialReverse from './Reverse';
import FinancialReceipt from './Receipt';
import { useConfiguration } from '../../hooks/configuration';
import { getSelectPaymentsMethods } from '../../http';
import { Screens } from '../../shared/enums/screens.enum';
import { useAuth } from '../../hooks/auth';
import dayjs from 'dayjs';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Financials: React.FC = () => {
  const { client_id } = useParams();
  const { configuration } = useConfiguration();
  const { screenFind, permissionFind } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { t } = useI18n();

  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<number | null>(1);
  const [type, setType] = useState<number | null>(1);
  const date = getInitialFinalDateMonth();
  const [date_initial, setDate_initial] = useState<string | null>(date.dateI);
  const [date_final, setDate_final] = useState<string | null>(date.dateF);
  const [filter_by, setFilter_by] = useState<string>('due_date');
  const [financials, setFinancials] = useState<IFinancial[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const [modalReceipt, setModalReceipt] = useState<boolean>(false);
  const [modalPayment, setModalPayment] = useState<boolean>(false);
  const [modalCancel, setModalCancel] = useState<boolean>(false);
  const [modalReverse, setModalReverse] = useState<boolean>(false);

  const [payments, setPayments] = useState<IPaymentMethod[]>([]);
  const [selectRows, setSelectRows] = useState<IFinancial[]>([]);

  const [toPayTotal, setToPayTotal] = useState<number>(0);
  const [paiedTotal, setPaiedTotal] = useState<number>(0);
  const [toReceiveTotal, setToReceiveTotal] = useState<number>(0);
  const [toReceivedTotal, setReceivedTotal] = useState<number>(0);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IFinancial[]) => {
      setSelectRows(selectedRows);
    },
  };

  const listPayments = useCallback(async () => {
    try {
      const data = await getSelectPaymentsMethods();
      setPayments(data);
    } catch {
      addToast({
        type: 'error',
        title: t('financialsPage.toast.errorTitle'),
        description: t('financialsPage.toast.listPayments'),
      });
    }
  }, [addToast, t]);

  const paymentSelected = useCallback(
    (value: number, operationType: 'pay' | 'canc') => {
      if (!selectRows.length) {
        addToast({
          type: 'warning',
          title: t('financialsPage.toast.warningTitle'),
          description: t('financialsPage.toast.selectAtLeastOne'),
        });
        return;
      }

      const canceled = selectRows.filter((x) => x.status === 5);
      if (canceled.length) {
        addToast({
          type: 'warning',
          title: t('financialsPage.toast.warningTitle'),
          description: t('financialsPage.toast.removeCancelled'),
        });
        return;
      }

      if (operationType === 'pay') {
        const pending = selectRows.filter((x) => x.status === value);
        if (pending.length !== selectRows.length) {
          addToast({
            type: 'warning',
            title: t('financialsPage.toast.warningTitle'),
            description: t('financialsPage.toast.selectPendingOnly'),
          });
          return;
        }
        setModalPayment(true);
      } else {
        setModalCancel(true);
      }
    },
    [selectRows, addToast, t],
  );

  const loadFilter = useCallback(() => {
    if (client_id) deleteFilter();
    const filter = getFilter();
    if (filter) {
      setStatus(filter.status ?? 1);
      setType(filter.type ?? 1);
      setFilter_by(filter.filter_by ?? 'due_date');
      setDate_initial(filter.date_initial ?? date.dateI);
      setDate_final(filter.date_final ?? date.dateF);
      setSearch(filter.keyword ?? '');
    }
  }, [client_id, date.dateI, date.dateF]);

  const requestListFinancial = async (filter: ISessionFilter | null) => {
    try {
      const response = await api.post('/financials/list', {
        take: limit,
        skip: (page - 1) * limit,
        ...filter,
      });
      const { count, data, paied, received, toPay, toReceive } = response.data;
      setPages(count);
      setFinancials(data);
      setPaiedTotal(paied);
      setToPayTotal(toPay);
      setToReceiveTotal(toReceive);
      setReceivedTotal(received);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('financialsPage.toast.errorTitle'),
        description: t('financialsPage.toast.listAccounts'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(() => {
    setPage(1);
    const filterData = {
      keyword: search ?? '',
      status,
      type,
      filter_by,
      date_initial,
      date_final,
      client_id,
    };
    if (!client_id) saveFilter(filterData);
    requestListFinancial(filterData);
  }, [
    search,
    client_id,
    status,
    type,
    filter_by,
    date_initial,
    date_final,
    limit,
    page,
  ]);

  const clearFilters = useCallback(() => {
    setType(null);
    setStatus(null);
    setFilter_by('due_date');
    setDate_initial(date.dateI);
    setDate_final(date.dateF);
  }, []);

  const list = useCallback(() => {
    setLoading(true);
    setFinancials([]);
    const filter = getFilter() ?? {
      keyword: search,
      status,
      type,
      filter_by,
      date_initial,
      date_final,
      client_id,
    };
    requestListFinancial(filter);
  }, [
    page,
    limit,
    search,
    status,
    type,
    filter_by,
    date_initial,
    date_final,
    client_id,
  ]);

  useEffect(() => {
    list();
  }, [page, limit]);

  useEffect(() => {
    loadFilter();
    listPayments();
  }, []);

  const renderStatus = (cell: IFinancial) => {
    const due = new Date(cell.due_date);
    const today = new Date();

    if (cell.status === 5)
      return <Tag color="red">{t('financialsPage.status.cancelled')}</Tag>;
    if (cell.status === 2)
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          {t('financialsPage.status.received')}
        </Tag>
      );
    if (cell.status === 1) {
      if (due < today)
        return (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            {t('financialsPage.status.overdue')}
          </Tag>
        );
      return <Tag color="blue">{t('financialsPage.status.pending')}</Tag>;
    }
    return <Tag>{cell.status}</Tag>;
  };

  const renderOptionsFinancial = (item: IFinancial) => {
    const options: MenuProps['items'] = [];

    if (
      item.status !== 2 &&
      item.status !== 5 &&
      permissionFind([PermissionActions.financialsUpdate])
    )
      options.push({
        label: t('financialsPage.actions.edit'),
        key: '1',
        icon: <EditOutlined />,
        onClick: () => navigate(`/financial/${item.id}`),
      });

    if (
      item.status === 1 &&
      permissionFind([PermissionActions.financialsPay])
    ) {
      options.push({
        label: t('financialsPage.actions.pay'),
        key: '2',
        icon: <DollarCircleOutlined />,
        onClick: () => {
          setSelectRows([item]);
          setModalPayment(true);
        },
      });
    }
    if (
      item.status === 2 &&
      permissionFind([PermissionActions.financialsCancel])
    ) {
      options.push({
        label: t('financialsPage.actions.reverse'),
        key: '3',
        icon: <RedoOutlined />,
        onClick: () => {
          setSelectRows([item]);
          setModalReverse(true);
        },
      });
    }
    if (item.type === 1 && item.status === 2) {
      options.push({
        label: t('financialsPage.actions.receipt'),
        key: '4',
        icon: <MdReceiptLong />,
        onClick: () => {
          setSelectRows([item]);
          setModalReceipt(true);
        },
      });
    }

    return options;
  };

  const menuItems: MenuProps['items'] = [
    ...(permissionFind([PermissionActions.financialsCreate])
      ? [
          {
            label: t('financialsPage.actions.newEntry'),
            key: '2',
            icon: <PlusCircleOutlined />,
            onClick: () => navigate(`/financial/new`),
          },
        ]
      : []),
    ...(screenFind([Screens.cashRegisterView])
      ? [
          {
            label: t('financialsPage.actions.cashRegister'),
            key: '2-0',
            icon: <FundOutlined />,
            onClick: () => navigate('/financial/cash-register'),
          },
        ]
      : []),
    ...(screenFind([Screens.quickSaleView])
      ? [
          {
            label: t('financialsPage.actions.quickSale'),
            key: '2-1',
            icon: <DollarCircleOutlined />,
            onClick: () => navigate('/sale'),
          },
        ]
      : []),
    ...(permissionFind([PermissionActions.financialsPay])
      ? [
          {
            label: t('financialsPage.actions.paySelected'),
            key: '3',
            icon: <CheckCircleOutlined />,
            onClick: () => paymentSelected(1, 'pay'),
            disabled: status === 2,
          },
        ]
      : []),
    ...(permissionFind([PermissionActions.financialsCancel])
      ? [
          {
            label: t('financialsPage.actions.cancelSelected'),
            key: '4',
            icon: <CloseCircleOutlined />,
            onClick: () => paymentSelected(2, 'canc'),
            disabled: status === 5,
          },
        ]
      : []),
    {
      label: t('financialsPage.actions.reports'),
      key: '5',
      icon: <PrinterOutlined />,
      children: [
        {
          label: t('financialsPage.actions.cashFlow'),
          key: '5-1',
          onClick: () => navigate('/financial/reports/cash-flow'),
        },
        {
          label: t('financialsPage.actions.dre'),
          key: '5-2',
          onClick: () => navigate('/financial/reports/dre'),
        },
        {
          label: t('financialsPage.actions.accountsPayable'),
          key: '5-3',
          onClick: () => navigate('/financial/reports/accounts-payable'),
        },
        {
          label: t('financialsPage.actions.accountsReceivable'),
          key: '5-4',
          onClick: () => navigate('/financial/reports/accounts-receivable'),
        },
        ...(screenFind([Screens.movementsFinancialsView])
          ? [
              {
                label: t('financialsPage.actions.movements'),
                key: '5-5',
                onClick: () => navigate('/financial/reports/movements'),
              },
            ]
          : []),
        {
          label: t('financialsPage.actions.delinquency'),
          key: '5-6',
          onClick: () => navigate('/financial/reports/delinquency'),
        },
        {
          label: t('financialsPage.actions.advancedReports'),
          key: '5-7',
          onClick: () => navigate('/financial/reports/advanced'),
        },
        {
          label: t('financialsPage.actions.commissionClosing'),
          key: '5-8',
          onClick: () => navigate('/financial/reports/commission-closing'),
        },
      ],
    },
  ];

  return (
    <FinancialsLayout>
      <PageIntro
        badge={t('financialsPage.intro.badge')}
        icon={<BankOutlined />}
        title={t('financialsPage.intro.title')}
        description={t('financialsPage.intro.description')}
        actions={
          !client_id ? (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button icon={<BarsOutlined />} type="primary">
                {t('financialsPage.actions.moreActions')}
              </Button>
            </Dropdown>
          ) : undefined
        }
      />

      <PagePanel>
        <FinancialsToolbar>
          <div style={{ flex: 1, minWidth: 260 }}>
            {client_id ? (
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                {t('financialsPage.actions.back')}
              </Button>
            ) : (
              <Input
                placeholder={t('financialsPage.filters.searchPlaceholder')}
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            )}
          </div>
        </FinancialsToolbar>

        <Collapse
          bordered={false}
          defaultActiveKey={[]}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{
            background: token.colorFillAlter,
            borderRadius: token.borderRadiusLG,
            border: 'none',
          }}
          items={[
            {
              key: '1',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  {t('financialsPage.filters.advanced')}
                </span>
              ),
              style: {
                border: 'none',
                borderRadius: token.borderRadiusLG,
              },
              children: (
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12} md={3}>
                    <Select
                      value={type}
                      onChange={setType}
                      placeholder={t('financialsPage.filters.typePlaceholder')}
                      style={{ width: '100%' }}
                      allowClear
                      options={[
                        {
                          value: 1,
                          label: t('financialsPage.filters.typeIncome'),
                        },
                        {
                          value: 2,
                          label: t('financialsPage.filters.typeExpense'),
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={3}>
                    <Select
                      value={status}
                      onChange={setStatus}
                      placeholder={t(
                        'financialsPage.filters.statusPlaceholder',
                      )}
                      style={{ width: '100%' }}
                      allowClear
                      options={[
                        { value: 1, label: t('financialsPage.status.open') },
                        { value: 2, label: t('financialsPage.status.paid') },
                        {
                          value: 3,
                          label: t('financialsPage.status.openPaid'),
                        },
                        {
                          value: 5,
                          label: t('financialsPage.status.cancelled'),
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Select
                      value={filter_by}
                      onChange={setFilter_by}
                      placeholder={t(
                        'financialsPage.filters.filterByPlaceholder',
                      )}
                      style={{ width: '100%' }}
                      options={[
                        {
                          value: 'due_date',
                          label: t('financialsPage.filters.dueDate'),
                        },
                        {
                          value: 'payment_date',
                          label: t('financialsPage.filters.paymentDate'),
                        },
                        {
                          value: 'competence_date',
                          label: t('financialsPage.filters.competenceDate'),
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <DatePicker.RangePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      value={[
                        date_initial ? dayjs(date_initial) : null,
                        date_final ? dayjs(date_final) : null,
                      ]}
                      onChange={(dates) => {
                        setDate_initial(
                          dates
                            ? (dates[0]?.format('YYYY-MM-DD') ?? null)
                            : null,
                        );
                        setDate_final(
                          dates
                            ? (dates[1]?.format('YYYY-MM-DD') ?? null)
                            : null,
                        );
                      }}
                      allowClear={false}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        width: '100%',
                      }}
                    >
                      <Button
                        icon={<ClearOutlined />}
                        onClick={clearFilters}
                        style={{ flex: 1 }}
                      >
                        {t('financialsPage.actions.clear')}
                      </Button>
                      <Button
                        type="primary"
                        icon={<RedoOutlined />}
                        onClick={handleSearch}
                        style={{ flex: 1 }}
                      >
                        {t('financialsPage.actions.filter')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </PagePanel>

      <SummaryGrid>
        {toReceivedTotal > 0 && (
          <StatisticCardContainer>
            <Statistic
              title={t('financialsPage.summary.confirmedReceived')}
              value={toReceivedTotal}
              precision={2}
              valueStyle={{ color: '#3f8600', fontWeight: 'bold' }}
              prefix={<ArrowDownOutlined />}
              formatter={(val) => currency(val)}
            />
          </StatisticCardContainer>
        )}
        {toReceiveTotal > 0 && (
          <StatisticCardContainer>
            <Statistic
              title={t('financialsPage.summary.pendingReceivable')}
              value={toReceiveTotal}
              precision={2}
              valueStyle={{ color: '#faad14', fontWeight: 'bold' }}
              prefix={<FilterOutlined />}
              formatter={(val) => currency(val)}
            />
          </StatisticCardContainer>
        )}
        {paiedTotal > 0 && (
          <StatisticCardContainer>
            <Statistic
              title={t('financialsPage.summary.paidExpenses')}
              value={paiedTotal}
              precision={2}
              valueStyle={{ color: '#cf1322', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined />}
              formatter={(val) => currency(val)}
            />
          </StatisticCardContainer>
        )}
        {toPayTotal > 0 && (
          <StatisticCardContainer>
            <Statistic
              title={t('financialsPage.summary.pendingPayable')}
              value={toPayTotal}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ArrowUpOutlined />}
              formatter={(val) => currency(val)}
            />
          </StatisticCardContainer>
        )}
      </SummaryGrid>

      <PagePanel>
        <ATable
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          scroll={{ y: 550 }}
          columns={[
            {
              title: t('financialsPage.table.id'),
              dataIndex: 'code',
              width: 70,
              render: (val: number) => <TextTable>{val}</TextTable>,
            },
            {
              title: t('financialsPage.table.type'),
              dataIndex: 'type',
              width: 80,
              render: (val: number) => (
                <Tag color={val === 1 ? 'green' : 'red'}>
                  {val === 1
                    ? t('financialsPage.table.income')
                    : t('financialsPage.table.expense')}
                </Tag>
              ),
            },
            configuration?.show_client_code
              ? {
                  title: t('financialsPage.table.clientCode'),
                  dataIndex: 'client_code',
                  width: 80,
                }
              : { width: 0 },
            {
              title: t('financialsPage.table.clientSupplier'),
              dataIndex: 'client_name',
              width: 220,
              render: (val: string) => (
                <Tooltip title={val}>
                  <TextTable>{val}</TextTable>
                </Tooltip>
              ),
            },
            {
              title: t('financialsPage.table.dueDate'),
              dataIndex: 'due_date',
              width: 115,
              render: (val: string) => <TextTable>{formatDate(val)}</TextTable>,
            },
            {
              title: t('financialsPage.table.paymentDate'),
              dataIndex: 'payment_date',
              width: 115,
              render: (val: string | null) => (
                <TextTable>{val ? formatDate(val) : '-'}</TextTable>
              ),
            },
            {
              title: t('financialsPage.table.value'),
              dataIndex: 'value',
              width: 110,
              render: (val: string | number) => (
                <span style={{ fontWeight: 600 }}>{currency(val)}</span>
              ),
            },
            {
              title: t('financialsPage.table.paymentMethod'),
              dataIndex: 'payment',
              width: 140,
              render: (val: string) => (
                <TextTable>
                  {payments.find((x) => x.id === val)?.description || '-'}
                </TextTable>
              ),
            },
            {
              title: t('financialsPage.table.status'),
              width: 120,
              render: renderStatus,
            },
            {
              title: '',
              width: 80,
              fixed: 'right',
              render: (cell: IFinancial) => (
                <DropdownOptions items={renderOptionsFinancial(cell)} />
              ),
            },
          ]}
          loading={loading}
          dataSource={financials}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {modalPayment && (
        <FinancialPayment
          open={modalPayment}
          close={() => setModalPayment(false)}
          selectRows={selectRows}
          setSelectRows={setSelectRows}
          paymentsMethods={payments}
          setFinancials={setFinancials}
        />
      )}
      {modalCancel && (
        <Cancel
          open={modalCancel}
          close={() => setModalCancel(false)}
          selectRows={selectRows}
          setSelectRows={setSelectRows}
          setFinancials={setFinancials}
        />
      )}
      {modalReverse && (
        <FinancialReverse
          open={modalReverse}
          close={() => setModalReverse(false)}
          selectRows={selectRows}
          setSelectRows={setSelectRows}
          setFinancials={setFinancials}
        />
      )}
      {modalReceipt && (
        <FinancialReceipt
          open={modalReceipt}
          close={() => setModalReceipt(false)}
          selectRows={selectRows}
        />
      )}
    </FinancialsLayout>
  );
};

export default Financials;
